/**
 * MultiSectionTimetableGrid Component
 * Requirements: 1.1, 1.2, 1.3, 1.5, 6.1, 6.2, 6.3, 17.1-17.10 (substitution overlay)
 */

import { Box, Typography, styled } from '@mui/material';
import { useState, useCallback, useRef, KeyboardEvent } from 'react';
import type {
  DayOfWeek,
  ClassSectionWithRelations,
  TimetableRecordWithRelations,
  SchedulePeriod,
} from '@timetable/types';
import { PeriodCard } from './PeriodCard';
import { EmptySlotCell } from './EmptySlotCell';
import { BreakPeriodCard } from './BreakPeriodCard';
import { useSubstitutionOverlay } from '../../hooks/useSubstitutionOverlay';
import { useAuthStore } from '../../store/authStore';
import type { Conflict } from './ConflictVisualizationPanel';

export interface MultiSectionTimetableGridProps {
  classId: string;
  editMode: boolean;
  temporalDate: Date | null;
  sections: ClassSectionWithRelations[];
  schedulePeriods: SchedulePeriod[];
  workingDays: DayOfWeek[];
  records: TimetableRecordWithRelations[];
  conflicts?: Conflict[];
  onPeriodClick: (sectionId: string, periodId: string, day: DayOfWeek) => void;
  onPeriodEdit: (record: TimetableRecordWithRelations) => void;
}

interface FocusedCell {
  sectionIndex: number;
  periodIndex: number;
  dayIndex: number;
}

// ── Styled components ────────────────────────────────────────────────────────

const HeaderCell = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 4,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(0.5, 1),
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const DayHeader = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 4,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(0.75, 1),
  borderRadius: theme.shape.borderRadius,
  textAlign: 'center',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const TimeLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(0.25, 0.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.7rem',
  color: theme.palette.text.secondary,
}));

const PeriodCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFocused',
})<{ isFocused?: boolean }>(({ theme, isFocused }) => ({
  minHeight: '52px',
  display: 'flex',
  alignItems: 'stretch',
  outline: isFocused ? `3px solid ${theme.palette.primary.main}` : 'none',
  outlineOffset: '-3px',
  transition: 'outline 0.15s',
  cursor: 'pointer',
  '& > *': {
    flex: 1,
  },
  '&:focus': {
    outline: `3px solid ${theme.palette.primary.main}`,
    outlineOffset: '-3px',
  },
}));

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(time: string | null | undefined): string {
  if (!time) return '--:--';
  return time.substring(0, 5);
}

// Deduplicate schedule periods by period_number, sorted by time_start
function getUniqueTimeslots(schedulePeriods: SchedulePeriod[]): SchedulePeriod[] {
  const seen = new Set<number>();
  const unique: SchedulePeriod[] = [];
  for (const sp of [...schedulePeriods].sort((a, b) => (a.time_start || '').localeCompare(b.time_start || ''))) {
    if (!seen.has(sp.period_number)) {
      seen.add(sp.period_number);
      unique.push(sp);
    }
  }
  return unique;
}

// ── Main Component ───────────────────────────────────────────────────────────

export function MultiSectionTimetableGrid({
  editMode,
  temporalDate,
  sections,
  schedulePeriods,
  workingDays,
  records,
  conflicts = [],
  onPeriodClick,
  onPeriodEdit,
}: MultiSectionTimetableGridProps) {
  const { schoolContext } = useAuthStore();
  const [focusedCell, setFocusedCell] = useState<FocusedCell | null>(null);
  const cellRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Substitution overlay hook (Requirements 17.1, 17.4, 17.5)
  const { getSubstitution, isLoading: loadingSubstitutions } = useSubstitutionOverlay(
    schoolContext,
    temporalDate
  );

  // Unique time slots (one row per period_number), sorted by time_start
  const timeslots = getUniqueTimeslots(schedulePeriods);

  const getCellKey = (si: number, pi: number, di: number) => `${si}-${pi}-${di}`;

  // Find record: match by section + day + period_number via joined period data
  const findRecord = (sectionId: string, periodNumber: number, day: DayOfWeek): TimetableRecordWithRelations | undefined => {
    return records.find(
      (r) => r.class_section_id === sectionId &&
             r.day_of_week === day &&
             r.period?.period_number === periodNumber
    );
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!focusedCell) return;
      const { sectionIndex, periodIndex, dayIndex } = focusedCell;
      let newFocus: FocusedCell | null = null;

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          if (periodIndex > 0) newFocus = { sectionIndex, periodIndex: periodIndex - 1, dayIndex };
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (periodIndex < timeslots.length - 1) newFocus = { sectionIndex, periodIndex: periodIndex + 1, dayIndex };
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (dayIndex > 0) newFocus = { sectionIndex, periodIndex, dayIndex: dayIndex - 1 };
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (dayIndex < workingDays.length - 1) newFocus = { sectionIndex, periodIndex, dayIndex: dayIndex + 1 };
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (editMode) {
            const section = sections[sectionIndex];
            const timeslot = timeslots[periodIndex];
            const day = workingDays[dayIndex];
            const record = findRecord(section.id, timeslot.period_number, day);
            const periodId = schedulePeriods.find(sp => sp.period_number === timeslot.period_number)?.id;
            if (record) onPeriodEdit(record);
            else if (periodId) onPeriodClick(section.id, periodId, day);
          }
          break;
        case 'Escape':
          event.preventDefault();
          setFocusedCell(null);
          break;
        default:
          return;
      }

      if (newFocus) {
        setFocusedCell(newFocus);
        const el = cellRefs.current.get(getCellKey(newFocus.sectionIndex, newFocus.periodIndex, newFocus.dayIndex));
        el?.focus();
      }
    },
    [focusedCell, sections, timeslots, workingDays, editMode, onPeriodClick, onPeriodEdit, schedulePeriods]
  );

  // Grid column template: Section col + Time col + one col per day
  // Section col is narrow since the label is rotated vertically
  const gridCols = `36px 52px repeat(${workingDays.length}, minmax(100px, 1fr))`;

  return (
    <Box
      role="grid"
      aria-label="Multi-section timetable grid"
      onKeyDown={handleKeyDown}
      sx={{ overflow: 'visible', width: '100%', minWidth: 'max-content' }}
    >
      {sections.map((section, sectionIndex) => {
        const rowCount = timeslots.length;
        return (
          <Box
            key={section.id}
            sx={{
              mb: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              // Single unified grid: 1 header row + N period rows
              display: 'grid',
              gridTemplateColumns: gridCols,
              // Row 1 = header, rows 2..N+1 = periods
              gridTemplateRows: `auto repeat(${rowCount}, auto)`,
            }}
          >
            {/* ── Header row ── */}
            {/* Blank spacer in col 1 of header row — section label spans body rows below */}
            <Box
              sx={{
                gridColumn: 1,
                gridRow: 1,
                borderRight: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
                position: 'sticky',
                top: 0,
                zIndex: 4,
              }}
            />

            {/* Time header */}
            <HeaderCell
              sx={{
                borderRadius: 0,
                borderRight: '1px solid',
                borderLeft: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
                gridColumn: 2,
                gridRow: 1,
                position: 'sticky',
                top: 0,
                zIndex: 4,
              }}
            >
              <Typography variant="caption" color="text.secondary">Time</Typography>
            </HeaderCell>

            {/* Day headers */}
            {workingDays.map((day, di) => (
              <DayHeader
                key={day}
                sx={{
                  borderRadius: 0,
                  margin: 0,
                  borderRight: di < workingDays.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  gridColumn: 3 + di,
                  gridRow: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 4,
                }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                  {day.charAt(0) + day.slice(1).toLowerCase()}
                </Typography>
              </DayHeader>
            ))}

            {/* ── Section label spanning all period rows ── */}
            <Box
              sx={{
                gridColumn: 1,
                gridRow: `2 / span ${rowCount}`,
                borderRight: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
                transform: 'rotate(180deg)',
                px: 0.5,
              }}
            >
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem' }} noWrap>
                {section.class?.class_name}-{section.section}
              </Typography>
            </Box>

            {/* ── Period rows ── */}
            {timeslots.map((timeslot, periodIndex) => (
              <>
                {/* Time cell */}
                <TimeLabel
                  key={`time-${timeslot.period_number}`}
                  sx={{
                    borderRight: '1px solid',
                    borderLeft: '1px solid',
                    borderBottom: periodIndex < timeslots.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    borderRadius: 0,
                    gridColumn: 2,
                    gridRow: 2 + periodIndex,
                  }}
                >
                  <Typography variant="caption" fontWeight={500} sx={{ fontSize: '0.65rem' }}>
                    {formatTime(timeslot.time_start)}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
                    {formatTime(timeslot.time_end)}
                  </Typography>
                </TimeLabel>

                {/* Day cells */}
                {workingDays.map((day, dayIndex) => {
                  const record = findRecord(section.id, timeslot.period_number, day);
                  const isFocused =
                    focusedCell?.sectionIndex === sectionIndex &&
                    focusedCell?.periodIndex === periodIndex &&
                    focusedCell?.dayIndex === dayIndex;
                  const cellKey = getCellKey(sectionIndex, periodIndex, dayIndex);

                  // Substitution overlay logic (Requirements 17.2, 17.3)
                  let substitution = null;
                  if (temporalDate && record) {
                    substitution = getSubstitution(day, record.period_id, undefined, record.class_section_id);
                  }

                  return (
                    <PeriodCell
                      key={`${section.id}-${timeslot.period_number}-${day}`}
                      ref={(el: HTMLDivElement | null) => {
                        if (el) cellRefs.current.set(cellKey, el);
                        else cellRefs.current.delete(cellKey);
                      }}
                      // Only show outer focus ring in edit mode AND when there's a record
                      // (EmptySlotCell manages its own border styling)
                      isFocused={editMode && isFocused && !!record}
                      tabIndex={isFocused ? 0 : -1}
                      role="gridcell"
                      aria-label={record ? `${record.subject?.subject_name} with ${record.teacher?.name}` : 'Empty slot'}
                      sx={{
                        borderRight: dayIndex < workingDays.length - 1 ? '1px solid' : 'none',
                        borderBottom: periodIndex < timeslots.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        borderRadius: 0,
                        p: 0.5,
                        gridColumn: 3 + dayIndex,
                        gridRow: 2 + periodIndex,
                      }}
                      onClick={() => {
                        // Only track focused cell in edit mode to avoid selection rectangle in view mode
                        if (editMode) {
                          setFocusedCell({ sectionIndex, periodIndex, dayIndex });
                          const periodId = schedulePeriods.find(sp => sp.period_number === timeslot.period_number)?.id;
                          if (record) onPeriodEdit(record);
                          else if (periodId) onPeriodClick(section.id, periodId, day);
                        }
                      }}
                    >
                      {timeslot.is_break ? (
                        <BreakPeriodCard period={timeslot} />
                      ) : record ? (
                        <PeriodCard
                          record={record}
                          viewMode="class"
                          isSelected={isFocused}
                          conflicts={conflicts.filter(c => c.affectedRecords.some(r => r.id === record.id))}
                          onEdit={editMode ? onPeriodEdit : undefined}
                          substitution={substitution}
                          isOverlayMode={!!temporalDate && !!substitution}
                        />
                      ) : (
                        <EmptySlotCell
                          editMode={editMode}
                          onClick={() => {
                            if (editMode) {
                              const periodId = schedulePeriods.find(sp => sp.period_number === timeslot.period_number)?.id;
                              if (periodId) onPeriodClick(section.id, periodId, day);
                            }
                          }}
                        />
                      )}
                    </PeriodCell>
                  );
                })}
              </>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
